import { Controller, type Control, type UseFormRegister } from 'react-hook-form';
import { Link } from 'react-router';

import { VocabularyPicker } from '@shared/ui/picker';
import { Select } from '@shared/ui/select';

import { Field, FIELD_CONTROL_CLASS } from './components';
import { sharedCopy } from './copy';
import type { TitleFormValues } from './forms.schema';
import type { TaxonomyTerm } from './types';

/**
 * The fields both authoring surfaces draw, in the order the catalog names them.
 *
 * They live here rather than in one page because the authoring form and the edit
 * form edit the **same record**: two copies would drift on the first field
 * added, and the drift would be invisible until an operator noticed one surface
 * could set something the other could not.
 *
 * **The required mark means «before publication», not «before saving».** A save
 * produces a draft and a draft is allowed to be incomplete; nothing here gates
 * the submit, and the rail beside the form is what says which fields remain.
 *
 * The category and the tag vocabularies are **managed elsewhere and read here**
 * — the hints name the surface that owns each, so an operator who needs a term
 * that does not exist knows where to go rather than typing one into a field that
 * would silently split a listing across two spellings.
 *
 * **Two columns from `--bp-xl`, one below it.** The authoring column is roughly
 * eleven hundred pixels wide on a desktop display and every control in it is
 * capped at the measure, which left a bare band running the height of the form
 * beside fields that had nowhere to grow. Pairing the short fields fills that
 * band without stretching any single control past the width it reads at; the
 * title and the description still span the row, because a one-line title field
 * half the width of its own description reads as two unrelated controls.
 */
export function TitleFields({
  register,
  control,
  categories,
  tags,
  ageRatings,
  fieldErrors = {},
}: {
  register: UseFormRegister<TitleFormValues>;
  /**
   * The category and the tag field are **controlled**, so they need the form's
   * control rather than its register.
   *
   * Both used to be native `<select>` elements and both were defects for the
   * same reason: the browser draws that control's popup, so the console could
   * neither place it under the field nor turn its arrow when it opened — and the
   * multiple variant demanded a held modifier key to pick a second tag, which is
   * a keyboard convention no operator was told about and no touch device has.
   */
  control: Control<TitleFormValues>;
  categories: readonly TaxonomyTerm[];
  tags: readonly TaxonomyTerm[];
  /**
   * The permitted age ratings for the **active type**, as the service publishes them.
   *
   * Empty means the schema has not been read yet, not that the field is open: the
   * control falls back to a text input so the form stays usable, and the service's
   * own refusal is what reaches the operator in that window.
   */
  ageRatings: readonly string[];
  /**
   * What the service refused, by field.
   *
   * Drawn beside the control that caused it rather than as one page-level line:
   * a rejection the operator cannot locate is a rejection of the whole form, and
   * on this surface the whole form can include a finished upload.
   */
  fieldErrors?: Record<string, string>;
}) {
  return (
    <>
      <section aria-labelledby="title-core" className="grid gap-5 xl:grid-cols-2 xl:items-start">
        <h2 id="title-core" className="font-display text-h3 font-bold xl:col-span-2">
          {sharedCopy.sections.core}
        </h2>

        <div className="xl:col-span-2">
          <Field label={sharedCopy.fields.title} required wide error={fieldErrors.title}>
            {(id, describedBy, invalid) => (
              <input
                id={id}
                type="text"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                className={FIELD_CONTROL_CLASS}
                {...register('title')}
              />
            )}
          </Field>
        </div>

        <div className="xl:col-span-2">
          <Field label={sharedCopy.fields.description} required wide error={fieldErrors.description}>
            {(id, describedBy, invalid) => (
              <textarea
                id={id}
                rows={5}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                className={`${FIELD_CONTROL_CLASS} min-h-[8rem] py-3`}
                {...register('description')}
              />
            )}
          </Field>
        </div>

        <Field
          label={sharedCopy.fields.category}
          required
          error={fieldErrors.categoryIds}
          hint={
            <>
              من شجرة التصنيفات. تُدار مفرداتها من{' '}
              <Link to="/taxonomy/categories" className="text-accent-text">
                صفحة التصنيفات
              </Link>
              .
            </>
          }
        >
          {(id, describedBy, invalid) => (
            <Controller
              control={control}
              name="categoryId"
              render={({ field }) => (
                <Select
                  id={id}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={categories.map((category) => ({
                    value: category.id,
                    label: category.name,
                  }))}
                  placeholder={sharedCopy.fields.categoryPlaceholder}
                  describedBy={describedBy}
                  invalid={invalid}
                />
              )}
            />
          )}
        </Field>

        {/*
          Chips above, a search below, and one press per tag — **no modifier
          key**. The control it replaces was a `<select multiple>`, where adding
          a second tag meant holding Ctrl and losing the whole selection on a
          plain click. Nothing on the page said so, and nothing could: it is the
          browser's convention, not this console's.
        */}
        {/*
          The picker labels its own search box, so this heading is a `<span>`
          rather than a `<label>`: a `for` pointing at a wrapper that cannot be
          labelled is an association the browser drops silently, and clicking it
          would focus nothing.
        */}
        <div className="grid max-w-measure content-start gap-2">
          <span className="text-sm font-medium text-ink">{sharedCopy.fields.tags}</span>

          <Controller
            control={control}
            name="tagIds"
            render={({ field }) => (
              <VocabularyPicker
                mode="multi"
                options={tags.map((tag) => ({ id: tag.id, label: tag.name }))}
                value={field.value}
                onChange={field.onChange}
                copy={{
                  searchLabel: sharedCopy.fields.tagsSearch,
                  searchPlaceholder: sharedCopy.fields.tagsPlaceholder,
                  resultCount: sharedCopy.fields.tagsCount,
                  noResults: sharedCopy.fields.tagsNoResults,
                  remove: sharedCopy.fields.tagsRemove,
                }}
              />
            )}
          />

          <p className="mb-0 max-w-none text-xs text-ink-muted">
            اختر من الوسوم المُدارة. تُدار مفرداتها من{' '}
            <Link to="/taxonomy/tags" className="text-accent-text">
              صفحة الوسوم
            </Link>
            .
          </p>

          {fieldErrors.tagIds ? (
            <p className="mb-0 max-w-none text-xs text-danger">{fieldErrors.tagIds}</p>
          ) : null}
        </div>
      </section>

      <section aria-labelledby="title-meta" className="grid gap-5">
        <h2 id="title-meta" className="font-display text-h3 font-bold">
          {sharedCopy.sections.metadata}
        </h2>

        <div className="grid gap-5 sm:grid-cols-2 sm:items-start xl:grid-cols-3">
          <Field label={sharedCopy.fields.year} required error={fieldErrors.productionYear}>
            {(id, describedBy, invalid) => (
              <input
                id={id}
                type="text"
                inputMode="numeric"
                dir="ltr"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                className={`mono ${FIELD_CONTROL_CLASS}`}
                {...register('productionYear')}
              />
            )}
          </Field>

          {/*
            **A two-letter ISO 3166-1 code, not a country name.** The service
            declares this field's kind as `country_code` and refuses anything else,
            so the control says so and caps the length rather than accepting «اليمن»
            and failing on save. The value is folded to upper case on the way out.
          */}
          <Field
            label={sharedCopy.fields.country}
            required
            hint={sharedCopy.fields.countryHint}
            error={fieldErrors.producingCountry}
          >
            {(id, describedBy, invalid) => (
              <input
                id={id}
                type="text"
                dir="ltr"
                maxLength={2}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                className={`mono ${FIELD_CONTROL_CLASS}`}
                {...register('producingCountry')}
              />
            )}
          </Field>

          {/*
            **A list now, because the owning service publishes one.** This used to be
            a free-text field on the stated grounds that the vocabulary was code inside
            the owning service and «published on no route this console can read» — which
            was true, and was the defect: an operator typed `+16`, the save was refused
            as a schema violation, and nothing on the page could say which five values
            were meant. `GET /contentTypes` answers that now.
          */}
          <Field
            label={sharedCopy.fields.ageRating}
            required
            hint={sharedCopy.fields.ageRatingHint}
            error={fieldErrors.ageRating}
          >
            {(id, describedBy, invalid) =>
              ageRatings.length > 0 ? (
                <Controller
                  control={control}
                  name="ageRating"
                  render={({ field }) => (
                    <Select
                      id={id}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      options={ageRatings.map((rating) => ({ value: rating, label: rating }))}
                      placeholder={sharedCopy.fields.ageRatingPlaceholder}
                      describedBy={describedBy}
                      invalid={invalid}
                    />
                  )}
                />
              ) : (
                <input
                  id={id}
                  type="text"
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  className={FIELD_CONTROL_CLASS}
                  {...register('ageRating')}
                />
              )
            }
          </Field>
        </div>
      </section>
    </>
  );
}
